import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, projectsApi, socioApi, orgsApi,
  type ProjectSpec, type ProjectBootstrapResult, type Organization, type OrgMember } from "@/api/client";
import { AppShell } from "@/components/AppShell";

/**
 * Wizard unificado de criação de sociedade.
 *
 * Sidebar com abas + painel principal por aba.
 * Cada módulo opcional tem TOGGLE que liga/desliga o deploy.
 * Aba Review mostra o que será deployado em ordem de dependências.
 */

const MONTH_SECONDS = 30 * 24 * 60 * 60;
const DEFAULT_DECIMALS = 18;
const withDecimals = (amount: bigint, decimals: number) => amount * 10n ** BigInt(decimals);

/**
 * Beneficiário na distribuição. 2 modos:
 *  - `wallet` preenchido → recebe no deploy (transfer/vesting na hora).
 *  - só `email` (sem wallet) → convite unificado: gera um código que cria
 *    a conta + entra na org + materializa os tokens (lazy claim).
 * `kind` separa sócio de investidor (mesma mecânica, rótulo diferente).
 */
interface BeneficiaryRow {
  label: string;
  email: string;
  wallet: string;
  percent: string;
  kind: "socio" | "investidor";
}

interface State {
  slug: string;
  organizationId: string;
  tokenName: string;
  tokenSymbol: string;
  tokenDecimals: string;
  tokenSupply: string;
  beneficiaries: BeneficiaryRow[];
  vestingEnabled: boolean;
  cliffMonths: string;
  durationMonths: string;
  vestingManager: string;
  vestingTreasury: string;
  investorPoolEnabled: boolean;
  poolPercent1: string;
  poolPercent2: string;
  poolOwner: string;
  governanceEnabled: boolean;
  timelockDelayHours: string;
  votingDelayMinutes: string;   // delay até a votação abrir (0 = na hora)
  votingPeriodHours: string;    // quanto tempo a votação fica aberta
  quorumEnabled: boolean;
  quorumPercent: string;
  aaEnabled: boolean;
  paymasterSigner: string;
  paymasterOwner: string;
}

type TabKey = "identity" | "distribution" | "vesting" | "investors" | "governance" | "aa" | "review";

const TABS: { key: TabKey; label: string; required?: boolean }[] = [
  { key: "identity", label: "Identidade", required: true },
  { key: "distribution", label: "Distribuição", required: true },
  { key: "vesting", label: "Vesting" },
  { key: "investors", label: "Investidores" },
  { key: "governance", label: "Governança" },
  { key: "aa", label: "Smart Wallets (AA)" },
  { key: "review", label: "Review & Deploy", required: true },
];

function initialState(deployerAddress: string): State {
  return {
    slug: "",
    organizationId: "",
    tokenName: "",
    tokenSymbol: "",
    tokenDecimals: "18",
    tokenSupply: "1000000",
    beneficiaries: [{ label: "Você", email: "", wallet: deployerAddress, percent: "100", kind: "socio" }],
    vestingEnabled: true,
    cliffMonths: "12",
    durationMonths: "60",
    vestingManager: deployerAddress,
    vestingTreasury: deployerAddress,
    investorPoolEnabled: false,
    poolPercent1: "6",
    poolPercent2: "4",
    poolOwner: deployerAddress,
    governanceEnabled: false,
    timelockDelayHours: "48",
    votingDelayMinutes: "0",
    votingPeriodHours: "168",
    quorumEnabled: true,
    quorumPercent: "50",
    aaEnabled: false,
    paymasterSigner: deployerAddress,
    paymasterOwner: deployerAddress,
  };
}

export default function NewProject() {
  const navigate = useNavigate();
  const [s, setS] = useState<State>(() => initialState(""));
  const [activeTab, setActiveTab] = useState<TabKey>("identity");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProjectBootstrapResult | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);

  useEffect(() => {
    socioApi.me()
      .then((u) => setS(initialState(u.smartAccount ?? "")))
      .catch(() => undefined);
    orgsApi.mine()
      .then((r) => {
        const owned = r.organizations.filter((o) => o.role === "OWNER");
        setOrgs(owned);
        if (owned.length > 0) setS((p) => ({ ...p, organizationId: owned[0].id }));
      })
      .catch(() => undefined);
  }, []);

  // Carrega os membros da org selecionada (pro seletor de distribuição).
  useEffect(() => {
    if (!s.organizationId) return;
    orgsApi.members(s.organizationId)
      .then((r) => setMembers(r.members))
      .catch(() => setMembers([]));
  }, [s.organizationId]);

  const beneficiariesPercent = s.beneficiaries.reduce(
    (acc, b) => acc + (Number(b.percent || "0") || 0), 0);
  const poolPercent = s.investorPoolEnabled
    ? (Number(s.poolPercent1 || "0") || 0) + (Number(s.poolPercent2 || "0") || 0)
    : 0;
  const totalPercent = beneficiariesPercent + poolPercent;
  const distributionValid = Math.abs(totalPercent - 100) < 0.001;

  const update = (patch: Partial<State>) => setS((p) => ({ ...p, ...patch }));

  function updateBeneficiary(i: number, patch: Partial<BeneficiaryRow>) {
    setS((p) => ({
      ...p,
      beneficiaries: p.beneficiaries.map((b, idx) => idx === i ? { ...b, ...patch } : b),
    }));
  }

  async function onDeploy() {
    setError(null);
    if (!distributionValid) {
      setError(`Soma das alocações é ${totalPercent.toFixed(2)}% — precisa ser 100%`);
      setActiveTab("distribution");
      return;
    }
    const supplyTokens = BigInt(s.tokenSupply || "0");
    if (supplyTokens <= 0n) { setError("Supply inválido"); return; }

    const decimals = Number(s.tokenDecimals) || DEFAULT_DECIMALS;
    if (decimals < 0 || decimals > 18 || !Number.isInteger(decimals)) {
      setError("Decimais devem ser um inteiro entre 0 e 18");
      setActiveTab("identity");
      return;
    }

    if (!s.organizationId) {
      setError("Selecione a organização do projeto (aba Identidade).");
      setActiveTab("identity");
      return;
    }

    // Cada beneficiário precisa de carteira OU e-mail, e um nome.
    const invalid = s.beneficiaries.find(
      (b) => Number(b.percent) > 0 && !b.wallet && !b.email);
    if (invalid) {
      setError("Todo beneficiário precisa de uma carteira OU um e-mail (pra convite).");
      setActiveTab("distribution");
      return;
    }
    const missingName = s.beneficiaries.find(
      (b) => Number(b.percent) > 0 && !b.label.trim());
    if (missingName) {
      setError("Dê um nome a cada beneficiário (pra identificar de quem é a carteira).");
      setActiveTab("distribution");
      return;
    }

    // Com wallet → imediato; só e-mail → convite unificado (lazy claim).
    const beneficiaries = s.beneficiaries
      .filter((b) => Number(b.percent) > 0 && (b.wallet || b.email))
      .map((b) => ({
        label: b.label.trim(),
        email: b.email || undefined,
        wallet: b.wallet || undefined,
        kind: b.kind,
        allocationWei: percentToWei(supplyTokens, b.percent, decimals).toString(),
      }));

    const spec = {
      slug: s.slug,
      organizationId: s.organizationId,
      token: {
        name: s.tokenName,
        symbol: s.tokenSymbol,
        decimals,
        totalSupplyWei: withDecimals(supplyTokens, decimals).toString(),
      },
      beneficiaries,
      vesting: {
        enabled: s.vestingEnabled,
        cliffSeconds: Math.round(Number(s.cliffMonths) * MONTH_SECONDS),
        durationSeconds: Math.round(Number(s.durationMonths) * MONTH_SECONDS),
        manager: s.vestingManager,
        treasury: s.vestingTreasury,
      },
      investorPool: s.investorPoolEnabled ? {
        enabled: true,
        round1AllocWei: percentToWei(supplyTokens, s.poolPercent1, decimals).toString(),
        round2AllocWei: percentToWei(supplyTokens, s.poolPercent2, decimals).toString(),
        owner: s.poolOwner,
      } : undefined,
      governance: s.governanceEnabled ? {
        enabled: true,
        timelockDelaySeconds: Math.round(Number(s.timelockDelayHours) * 3600),
        quorumPercent: s.quorumEnabled ? (Number(s.quorumPercent) || 0) : 0,
        votingDelaySeconds: Math.round(Number(s.votingDelayMinutes) * 60),
        votingPeriodSeconds: Math.round(Number(s.votingPeriodHours) * 3600),
      } : undefined,
      aa: s.aaEnabled ? {
        enabled: true,
        paymasterSigner: s.paymasterSigner,
        paymasterOwner: s.paymasterOwner,
      } : undefined,
    };

    setLoading(true);
    try {
      const r = await projectsApi.bootstrap(spec as unknown as ProjectSpec);
      setResult(r);
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro inesperado");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return <AppShell><ResultView result={result} onNew={() => navigate("/admin/projects")} /></AppShell>;
  }

  const tabBadge = (tab: TabKey): string | null => {
    if (tab === "vesting") return s.vestingEnabled ? "on" : "off";
    if (tab === "investors") return s.investorPoolEnabled ? "on" : "off";
    if (tab === "governance") return s.governanceEnabled ? "on" : "off";
    if (tab === "aa") return s.aaEnabled ? "on" : "off";
    return null;
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6">
        <Link to="/admin/projects" className="text-sm underline text-app-muted">← projetos</Link>
        <h1 className="text-2xl font-bold my-4">Nova sociedade</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <nav className="space-y-1">
            {TABS.map((t) => {
              const badge = tabBadge(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between transition ${
                    activeTab === t.key
                      ? "bg-app-accent text-app-accent-text"
                      : "hover:bg-app-surface-2"
                  }`}
                >
                  <span>{t.label}</span>
                  {badge && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      badge === "on" ? "bg-green-500/20 text-green-700 dark:text-green-300" : "bg-app-border text-app-muted"
                    }`}>
                      {badge}
                    </span>
                  )}
                  {t.required && !badge && <span className="text-xs text-red-500">*</span>}
                </button>
              );
            })}
          </nav>

          <div className="app-card p-5 min-h-[500px]">
            {activeTab === "identity" && <IdentityTab s={s} update={update} orgs={orgs} />}
            {activeTab === "distribution" && (
              <DistributionTab s={s} update={update} updateBeneficiary={updateBeneficiary}
                               members={members}
                               beneficiariesPercent={beneficiariesPercent}
                               poolPercent={poolPercent} totalPercent={totalPercent}
                               valid={distributionValid} />
            )}
            {activeTab === "vesting" && <VestingTab s={s} update={update} />}
            {activeTab === "investors" && <InvestorsTab s={s} update={update} />}
            {activeTab === "governance" && <GovernanceTab s={s} update={update} />}
            {activeTab === "aa" && <AATab s={s} update={update} />}
            {activeTab === "review" && (
              <ReviewTab s={s} totalPercent={totalPercent} valid={distributionValid}
                         loading={loading} error={error} onDeploy={onDeploy} />
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────

function IdentityTab({ s, update, orgs }:
  { s: State; update: (p: Partial<State>) => void; orgs: Organization[] }) {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-1">Identidade da sociedade</h2>
      <p className="text-sm text-app-muted mb-4">Organização, nome, símbolo e supply do token.</p>

      <div className="space-y-4">
        <div>
          <Label>Organização</Label>
          {orgs.length === 0 ? (
            <p className="text-sm text-amber-600">
              Você precisa ser dono de uma organização pra criar projetos.{" "}
              <Link to="/organizacao" className="underline">Criar organização</Link>
            </p>
          ) : (
            <select value={s.organizationId} onChange={(e) => update({ organizationId: e.target.value })}
                    className="w-full px-3 py-2">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          )}
          <Helper>O projeto e seus membros ficam no escopo desta organização.</Helper>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Nome do projeto / token</Label>
            <Input value={s.tokenName} onChange={(v) => update({ tokenName: v })}
                   placeholder="ex: Goyaz Games" />
          </div>
          <div>
            <Label>Símbolo</Label>
            <Input value={s.tokenSymbol} onChange={(v) => update({ tokenSymbol: v.toUpperCase() })}
                   placeholder="ex: GOYA" maxLength={20} mono />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Supply total (tokens)</Label>
            <Input type="number" value={s.tokenSupply} onChange={(v) => update({ tokenSupply: v })}
                   min={1} />
            <Helper>Quantidade de tokens inteiros. Decimais aplicados automaticamente.</Helper>
          </div>
          <div>
            <Label>Decimais</Label>
            <div className="flex items-center gap-2">
              <input type="range" min={0} max={18} step={1}
                     value={Number(s.tokenDecimals) || 18}
                     onChange={(e) => update({ tokenDecimals: e.target.value })}
                     className="flex-1" />
              <input type="number" min={0} max={18} value={s.tokenDecimals}
                     onChange={(e) => update({ tokenDecimals: e.target.value })}
                     className="w-16 px-2 py-1 text-right" />
            </div>
            <Helper>
              18 = padrão ERC-20 · 6 = stablecoin · 0 = unidades indivisíveis
            </Helper>
          </div>
        </div>

        <div>
          <Label>Slug (identificador único)</Label>
          <Input value={s.slug} mono
                 onChange={(v) => update({ slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                 placeholder="ex: goyaz-games-v1" pattern="[a-z0-9-]{3,100}" />
          <Helper>Letras minúsculas, números, hífen.</Helper>
        </div>
      </div>
    </div>
  );
}

function DistributionTab(props: {
  s: State; update: (p: Partial<State>) => void;
  updateBeneficiary: (i: number, p: Partial<BeneficiaryRow>) => void;
  members: OrgMember[];
  beneficiariesPercent: number; poolPercent: number; totalPercent: number; valid: boolean;
}) {
  const { s, updateBeneficiary, members, beneficiariesPercent, poolPercent, totalPercent, valid } = props;

  function addRow(row?: Partial<BeneficiaryRow>) {
    props.update({ beneficiaries: [...s.beneficiaries,
      { label: "", email: "", wallet: "", percent: "", kind: "socio", ...row }] });
  }
  function removeRow(i: number) {
    props.update({ beneficiaries: s.beneficiaries.filter((_, idx) => idx !== i) });
  }

  // Membros que ainda não estão na lista (por wallet ou e-mail).
  const used = new Set(s.beneficiaries.flatMap((b) =>
    [b.wallet.toLowerCase(), b.email.toLowerCase()].filter(Boolean)));
  const available = members.filter((m) =>
    !used.has((m.smartAccount || "").toLowerCase()) && !used.has((m.email || "").toLowerCase()));

  function addMember(socioId: string) {
    const m = members.find((x) => x.socioId === socioId);
    if (!m) return;
    // Ativado → recebe direto na carteira; convidado → lazy via e-mail.
    addRow(m.activated
      ? { label: m.nome, wallet: m.smartAccount, kind: "socio" }
      : { label: m.nome, email: m.email, kind: "socio" });
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-1">Distribuição</h2>
      <p className="text-sm text-app-muted mb-4">
        Selecione <strong>membros da organização</strong> ou adicione gente nova. Quem tem
        <strong> carteira</strong> recebe na hora; quem entra por <strong>e-mail</strong> recebe um
        link pra criar a conta, entrar no grupo e fazer o claim. Soma + pool = 100%.
      </p>

      {available.length > 0 && (
        <div className="mb-3 flex items-center gap-2">
          <select defaultValue="" onChange={(e) => { if (e.target.value) { addMember(e.target.value); e.target.value = ""; } }}
                  className="text-sm px-3 py-2">
            <option value="">+ adicionar membro da organização…</option>
            {available.map((m) => (
              <option key={m.socioId} value={m.socioId}>
                {m.nome} {m.activated ? `· ${m.smartAccount.slice(0, 6)}…${m.smartAccount.slice(-4)}` : `· ${m.email} (convidado)`}
              </option>
            ))}
          </select>
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-app-border text-left text-xs uppercase text-app-muted">
            <th className="py-2 pr-2 w-1/6">Nome</th>
            <th className="pr-2 w-1/4">E-mail (convite)</th>
            <th className="pr-2 w-1/3">Carteira</th>
            <th className="pr-2 w-24">Tipo</th>
            <th className="pr-2 w-16 text-right">%</th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody>
          {s.beneficiaries.map((b, i) => (
            <tr key={i} className="border-b border-app-border last:border-0">
              <td className="py-1 pr-2">
                <Input value={b.label} onChange={(v) => updateBeneficiary(i, { label: v })}
                       placeholder="ex: Theo" small />
              </td>
              <td className="pr-2">
                <Input value={b.email}
                       onChange={(v) => updateBeneficiary(i, { email: v.toLowerCase().replace(/\s/g, "") })}
                       placeholder="email@... (sem carteira)" small mono />
              </td>
              <td className="pr-2">
                <Input value={b.wallet} onChange={(v) => updateBeneficiary(i, { wallet: v })}
                       placeholder="0x... (recebe na hora)" mono small
                       pattern="0x[a-fA-F0-9]{40}" />
              </td>
              <td className="pr-2">
                <select value={b.kind} onChange={(e) => updateBeneficiary(i, { kind: e.target.value as BeneficiaryRow["kind"] })}
                        className="w-full px-1 py-1 text-xs">
                  <option value="socio">Sócio</option>
                  <option value="investidor">Investidor</option>
                </select>
              </td>
              <td className="pr-2 text-right">
                <input type="number" value={b.percent} step={0.01} min={0} max={100}
                       onChange={(e) => updateBeneficiary(i, { percent: e.target.value })}
                       className="w-16 px-2 py-1 text-sm text-right" />
              </td>
              <td className="text-right">
                <button type="button" onClick={() => removeRow(i)}
                        className="text-red-500 hover:text-red-700 px-2">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between items-center mt-3 text-sm">
        <button type="button" onClick={() => addRow()}
                className="text-app-accent hover:opacity-80 underline">+ adicionar manual</button>
        <div className="text-right">
          <p>Beneficiários: <strong>{beneficiariesPercent.toFixed(2)}%</strong></p>
          {poolPercent > 0 && (
            <p className="text-xs text-app-muted">Pool investidores: {poolPercent.toFixed(2)}%</p>
          )}
          <p className={valid ? "text-green-600 font-semibold" : "text-red-600"}>
            Total: {totalPercent.toFixed(2)}% {valid ? "✓" : "(precisa 100%)"}
          </p>
        </div>
      </div>
    </div>
  );
}

function VestingTab({ s, update }: { s: State; update: (p: Partial<State>) => void }) {
  return (
    <div>
      <ModuleHeader title="Vesting"
        description="Tokens dos sócios ficam em contratos de vesting com cliff + duração. Quando desabilitado, tokens vão direto pras wallets."
        enabled={s.vestingEnabled} onToggle={(e) => update({ vestingEnabled: e })} />

      {s.vestingEnabled && (
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SliderField label="Cliff (meses)" value={s.cliffMonths}
                         onChange={(v) => update({ cliffMonths: v })}
                         min={0} max={60} unit="meses"
                         hint={(n) => `≈ ${(n/12).toFixed(1)} anos`} />
            <SliderField label="Duração total (meses)" value={s.durationMonths}
                         onChange={(v) => update({ durationMonths: v })}
                         min={1} max={120} unit="meses"
                         hint={(n) => `≈ ${(n/12).toFixed(1)} anos`} />
          </div>
          <div>
            <Label>Manager (quem pode revogar vestings)</Label>
            <Input value={s.vestingManager} onChange={(v) => update({ vestingManager: v })}
                   placeholder="0x..." mono pattern="0x[a-fA-F0-9]{40}" />
          </div>
          <div>
            <Label>Tesouraria (recebe revogados)</Label>
            <Input value={s.vestingTreasury} onChange={(v) => update({ vestingTreasury: v })}
                   placeholder="0x..." mono pattern="0x[a-fA-F0-9]{40}" />
          </div>
        </div>
      )}
    </div>
  );
}

function InvestorsTab({ s, update }: { s: State; update: (p: Partial<State>) => void }) {
  return (
    <div>
      <ModuleHeader title="Pool de investidores"
        description="Reserva parte do supply pra rodadas de investimento (2 pools com pesos diferentes)."
        enabled={s.investorPoolEnabled} onToggle={(e) => update({ investorPoolEnabled: e })} />

      {s.investorPoolEnabled && (
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>% Rodada 1</Label>
              <Input type="number" value={s.poolPercent1}
                     onChange={(v) => update({ poolPercent1: v })} min={0} max={100} step={0.01} />
            </div>
            <div>
              <Label>% Rodada 2</Label>
              <Input type="number" value={s.poolPercent2}
                     onChange={(v) => update({ poolPercent2: v })} min={0} max={100} step={0.01} />
            </div>
          </div>
          <div>
            <Label>Owner do pool</Label>
            <Input value={s.poolOwner} onChange={(v) => update({ poolOwner: v })}
                   placeholder="0x..." mono pattern="0x[a-fA-F0-9]{40}" />
          </div>
        </div>
      )}
    </div>
  );
}

function GovernanceTab({ s, update }: { s: State; update: (p: Partial<State>) => void }) {
  return (
    <div>
      <ModuleHeader title="Governança (Governor + Timelock)"
        description="DAO on-chain: sócios votam com peso do token, propostas passam por timelock antes de executar."
        enabled={s.governanceEnabled} onToggle={(e) => update({ governanceEnabled: e })} />

      {s.governanceEnabled && (
        <div className="space-y-4 mt-4">
          <SliderField label="Delay até a votação abrir (minutos)" value={s.votingDelayMinutes}
                       onChange={(v) => update({ votingDelayMinutes: v })}
                       min={0} max={1440} unit="min"
                       hint={(n) => n === 0 ? "abre na hora (sem delay)" : `≈ ${(n/60).toFixed(1)}h após criar`} />

          <SliderField label="Duração da votação (horas)" value={s.votingPeriodHours}
                       onChange={(v) => update({ votingPeriodHours: v })}
                       min={1} max={336} unit="horas"
                       hint={(n) => `≈ ${(n/24).toFixed(1)} dias de votação aberta`} />

          <SliderField label="Delay do Timelock (horas)" value={s.timelockDelayHours}
                       onChange={(v) => update({ timelockDelayHours: v })}
                       min={0} max={168} unit="horas"
                       hint={(n) => n === 0 ? "executa na hora (sem timelock)" : `≈ ${(n/24).toFixed(1)} dias`} />

          <div className="border-t border-app-border pt-4">
            <ModuleHeader title="Exigir quórum"
              description="Participação mínima pra uma votação valer. Desligado = basta maioria (a favor > contra), independente de quantos votaram."
              enabled={s.quorumEnabled} onToggle={(e) => update({ quorumEnabled: e })}
              size="sm" />

            {s.quorumEnabled && (
              <div className="mt-3">
                <SliderField label="Quórum (% do supply que precisa votar)"
                             value={s.quorumPercent}
                             onChange={(v) => update({ quorumPercent: v })}
                             min={1} max={100} unit="%" />
              </div>
            )}
          </div>

          <p className="text-xs text-app-muted border-t border-app-border pt-3">
            Toda decisão é por <strong>maioria do poder de voto</strong> (a favor &gt; contra).
            Não há voto de veto nem categoria especial — quem somar mais voto vence.
          </p>
        </div>
      )}
    </div>
  );
}

function AATab({ s, update }: { s: State; update: (p: Partial<State>) => void }) {
  return (
    <div>
      <ModuleHeader title="Account Abstraction (ERC-4337)"
        description="Stack de smart wallets — sócios usam contas inteligentes em vez de chaves EOA. EntryPoint + Factory + Paymaster."
        enabled={s.aaEnabled} onToggle={(e) => update({ aaEnabled: e })} />

      {s.aaEnabled && (
        <div className="space-y-4 mt-4">
          <div>
            <Label>Paymaster signer</Label>
            <Input value={s.paymasterSigner} onChange={(v) => update({ paymasterSigner: v })}
                   placeholder="0x..." mono pattern="0x[a-fA-F0-9]{40}" />
            <Helper>Quem assina autorizações off-chain pra cobrir gas.</Helper>
          </div>
          <div>
            <Label>Paymaster owner</Label>
            <Input value={s.paymasterOwner} onChange={(v) => update({ paymasterOwner: v })}
                   placeholder="0x..." mono pattern="0x[a-fA-F0-9]{40}" />
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewTab(props: {
  s: State; totalPercent: number; valid: boolean;
  loading: boolean; error: string | null; onDeploy: () => void;
}) {
  const { s, totalPercent, valid, loading, error, onDeploy } = props;

  const modules = [
    { name: "EquityToken", enabled: true, desc: `${s.tokenName || "(sem nome)"} (${s.tokenSymbol || "?"})` },
    { name: "VestingFactory + Vestings",
      enabled: s.vestingEnabled,
      desc: `${s.beneficiaries.filter(b => Number(b.percent) > 0).length} cronograma(s)` },
    { name: "InvestorPool", enabled: s.investorPoolEnabled,
      desc: `${s.poolPercent1}% + ${s.poolPercent2}% do supply` },
    { name: "TimelockController + Governor", enabled: s.governanceEnabled,
      desc: `maioria · ${s.quorumEnabled ? `quórum ${s.quorumPercent}%` : "sem quórum"} · ` +
            `abre ${Number(s.votingDelayMinutes) === 0 ? "na hora" : `em ${s.votingDelayMinutes}min`} · ` +
            `votação ${s.votingPeriodHours}h · timelock ${s.timelockDelayHours}h` },
    { name: "EntryPoint + AccountFactory + Paymaster", enabled: s.aaEnabled,
      desc: "AA stack completo" },
  ];

  const willDeploy = modules.filter((m) => m.enabled);

  return (
    <div>
      <h2 className="text-xl font-semibold mb-1">Review & Deploy</h2>
      <p className="text-sm text-app-muted mb-4">
        Confira o que vai ser deployado. Ordem respeita dependências (Token primeiro,
        Vesting depende do Token, Governor depende do Token + Timelock, etc).
      </p>

      <div className="border border-app-border rounded p-4 mb-4">
        <h3 className="font-semibold text-sm mb-2">Vai deployar:</h3>
        <ul className="text-sm space-y-1">
          {willDeploy.map((m, i) => (
            <li key={m.name} className="flex items-baseline gap-2">
              <span className="text-app-muted w-5 text-right">{i + 1}.</span>
              <strong>{m.name}</strong>
              <span className="text-app-muted text-xs">— {m.desc}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border border-app-border rounded p-4 mb-4 text-sm">
        <p className="mb-1"><strong>Total a deployar:</strong> {willDeploy.length} contrato(s)</p>
        <p className="mb-1"><strong>Custo estimado:</strong> ~{(willDeploy.length * 0.0001).toFixed(4)} ETH (testnet)</p>
        <p className={valid ? "text-green-600" : "text-red-600"}>
          <strong>Distribuição:</strong> {totalPercent.toFixed(2)}% {valid ? "✓" : "(precisa 100%)"}
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded p-3 mb-3 text-sm text-red-600 whitespace-pre-wrap">
          {error}
        </div>
      )}

      <button type="button" onClick={onDeploy} disabled={loading || !valid}
              className="bg-app-accent text-app-accent-text rounded px-6 py-3 font-semibold disabled:opacity-50">
        {loading ? "Deployando..." : "Deploy"}
      </button>
    </div>
  );
}

function ResultView({ result, onNew }: { result: ProjectBootstrapResult; onNew: () => void }) {
  const explorer = "https://sepolia.basescan.org";
  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">✅ Sociedade criada!</h1>
      <p className="text-sm text-app-muted mb-4">Slug: <code>{result.slug}</code></p>

      <section className="border border-app-border bg-app-surface rounded-lg p-4 mb-4">
        <h2 className="font-semibold mb-2">Contratos deployados</h2>
        <ul className="space-y-1 text-sm">
          <li><strong>Token:</strong>{" "}
            <a className="font-mono text-app-accent underline" target="_blank" rel="noreferrer"
               href={`${explorer}/address/${result.tokenAddress}`}>{result.tokenAddress}</a>
          </li>
          {result.vestingFactoryAddress && (
            <li><strong>VestingFactory:</strong>{" "}
              <a className="font-mono text-app-accent underline" target="_blank" rel="noreferrer"
                 href={`${explorer}/address/${result.vestingFactoryAddress}`}>{result.vestingFactoryAddress}</a>
            </li>
          )}
        </ul>
      </section>

      {result.vestings && result.vestings.length > 0 && (
        <section className="border border-app-border bg-app-surface rounded-lg p-4 mb-4">
          <h2 className="font-semibold mb-2">Vestings ({result.vestings.length})</h2>
          {result.vestings.map((v) => (
            <div key={v.wallet} className="text-xs font-mono mb-1">
              {v.wallet} → <a className="text-app-accent underline" target="_blank" rel="noreferrer"
                              href={`${explorer}/address/${v.vestingAddress}`}>{v.vestingAddress}</a>
            </div>
          ))}
        </section>
      )}

      {result.invites && result.invites.length > 0 && (
        <section className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-4 mb-4">
          <h2 className="font-semibold mb-1">🎟️ Convites de ativação ({result.invites.length})</h2>
          <p className="text-xs text-app-muted mb-3">
            Entregue o <strong>código</strong> ou o <strong>link</strong> pra cada sócio/investidor
            que ainda não tem carteira. Só com ele a pessoa ativa a conta e recebe os tokens.
            <strong> Aparece só agora — copie antes de sair.</strong>
          </p>
          <div className="space-y-2">
            {result.invites.map((inv) => (
              <InviteRow key={inv.email} invite={inv} />
            ))}
          </div>
        </section>
      )}

      <button onClick={onNew} className="bg-app-accent text-app-accent-text rounded px-4 py-2">
        Ir pra lista de projetos
      </button>
    </div>
  );
}

function InviteRow({ invite }: { invite: { email: string; label: string; code: string } }) {
  const link = `${window.location.origin}/ativar?codigo=${invite.code}`;
  const [copied, setCopied] = useState<"" | "code" | "link">("");
  function copy(what: "code" | "link", value: string) {
    navigator.clipboard.writeText(value);
    setCopied(what);
    setTimeout(() => setCopied(""), 1500);
  }
  return (
    <div className="bg-app-surface border border-app-border rounded p-3 text-sm">
      <div className="font-medium mb-1">{invite.label} <span className="text-app-muted font-mono text-xs">{invite.email}</span></div>
      <div className="flex items-center gap-2 mb-1">
        <code className="flex-1 bg-app-surface-2 rounded px-2 py-1 font-mono tracking-wider">{invite.code}</code>
        <button type="button" onClick={() => copy("code", invite.code)}
                className="text-xs border border-app-border rounded px-2 py-1 hover:bg-app-surface-2">
          {copied === "code" ? "✓" : "copiar código"}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-app-surface-2 rounded px-2 py-1 text-xs truncate">{link}</code>
        <button type="button" onClick={() => copy("link", link)}
                className="text-xs border border-app-border rounded px-2 py-1 hover:bg-app-surface-2">
          {copied === "link" ? "✓" : "copiar link"}
        </button>
      </div>
    </div>
  );
}

// ─── Componentes auxiliares ──────────────────────────────────────

function Label({ children }: { children: ReactNode }) {
  return <label className="block text-sm font-medium mb-1">{children}</label>;
}
function Helper({ children }: { children: ReactNode }) {
  return <p className="text-xs text-app-muted mt-1">{children}</p>;
}

function Input(props: {
  value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; pattern?: string;
  mono?: boolean; small?: boolean; maxLength?: number;
  min?: number; max?: number; step?: number;
}) {
  return (
    <input
      type={props.type ?? "text"}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      pattern={props.pattern}
      maxLength={props.maxLength}
      min={props.min} max={props.max} step={props.step}
      className={`w-full bg-app-surface border border-app-border rounded px-3 py-2 text-app-text placeholder:text-app-muted ${
        props.mono ? "font-mono text-sm" : ""
      } ${props.small ? "py-1 text-sm" : ""}`}
    />
  );
}

function ModuleHeader(props: {
  title: string; description: string;
  enabled: boolean; onToggle: (e: boolean) => void;
  size?: "lg" | "sm";
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className={props.size === "sm" ? "text-lg font-semibold" : "text-xl font-semibold"}>
          {props.title}
        </h2>
        <p className="text-sm text-app-muted">{props.description}</p>
      </div>
      <button type="button" role="switch" aria-checked={props.enabled}
              onClick={() => props.onToggle(!props.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0 ${
                props.enabled ? "bg-green-500" : "bg-app-border"
              }`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          props.enabled ? "translate-x-6" : "translate-x-1"
        }`} />
      </button>
    </div>
  );
}

function SliderField(props: {
  label: string; value: string; onChange: (v: string) => void;
  min: number; max: number; unit: string; hint?: (n: number) => string;
}) {
  const n = Number(props.value || "0");
  return (
    <div>
      <Label>{props.label}</Label>
      <div className="flex items-center gap-3">
        <input type="range" min={props.min} max={props.max} step={1}
               value={n}
               onChange={(e) => props.onChange(e.target.value)}
               className="flex-1" />
        <input type="number" value={props.value}
               onChange={(e) => props.onChange(e.target.value)}
               min={props.min} max={props.max}
               className="w-20 bg-app-surface border border-app-border rounded px-2 py-1 text-right text-app-text" />
        <span className="text-xs text-app-muted w-12">{props.unit}</span>
      </div>
      {props.hint && <p className="text-xs text-app-muted mt-1">{props.hint(n)}</p>}
    </div>
  );
}

function percentToWei(supplyTokens: bigint, percentStr: string, decimals: number = DEFAULT_DECIMALS): bigint {
  const percent = Number(percentStr || "0");
  if (!Number.isFinite(percent) || percent < 0) return 0n;
  const basisPoints = BigInt(Math.round(percent * 100));
  return (withDecimals(supplyTokens, decimals) * basisPoints) / 10000n;
}
